import { z } from "zod";
import { shippingTextSchema } from "@/lib/shipping-text";

// Match Server's existing country-aware contact validator.
export function shipmentContactInputSchema(
  country: string,
  message = "Phone number is required or invalid"
) {
  return z.object({
    name: shippingTextSchema.trim().min(1, "Name is required"),
    phone: z
      .string()
      .trim()
      .min(1, message)
      .regex(/^\+?[\d\s().-]+$/, message)
      .transform((value, ctx) => {
        const digits = value.replace(/\D/g, "");
        const northAmerican = ["CA", "US"].includes(
          country.trim().toUpperCase()
        );
        const phone =
          northAmerican && digits.length === 11 && digits.startsWith("1")
            ? digits.slice(1)
            : digits;
        if (
          northAmerican
            ? phone.length !== 10
            : phone.length < 7 || phone.length > 15
        ) {
          ctx.addIssue({ code: "custom", message });
          return z.NEVER;
        }
        return phone;
      }),
    email: z
      .string()
      .trim()
      .pipe(z.string().email().or(z.literal("")))
      .optional(),
  });
}

export function normalizeShipmentContacts<
  T extends {
    senderName: string;
    senderPhone: string;
    senderEmail?: string;
    recipientName: string;
    recipientPhone: string;
    recipientEmail?: string;
    shipFrom: { country: string };
    shipTo: { country: string };
  },
>(shipment: T, ctx: z.RefinementCtx, message?: string): T {
  const normalized = { ...shipment };
  for (const [party, address] of [
    ["sender", shipment.shipFrom],
    ["recipient", shipment.shipTo],
  ] as const) {
    const contact = shipmentContactInputSchema(
      address.country,
      message
    ).safeParse({
      name: shipment[`${party}Name`],
      phone: shipment[`${party}Phone`],
      email: shipment[`${party}Email`],
    });
    if (!contact.success) {
      for (const issue of contact.error.issues) {
        const field = issue.path[0];
        const key =
          field === "name"
            ? `${party}Name`
            : field === "phone"
              ? `${party}Phone`
              : `${party}Email`;
        ctx.addIssue({ code: "custom", path: [key], message: issue.message });
      }
    } else {
      normalized[`${party}Name`] = contact.data.name;
      normalized[`${party}Phone`] = contact.data.phone;
      normalized[`${party}Email`] = contact.data.email;
    }
  }
  return normalized;
}
